import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Download } from "lucide-react";
import { API, statusLabel, fmtDate, STATUS_COLOR } from "./constants";
import { DataTable, Pager } from "./DataTable";

export function CandidatesTab({ presetStatus, onOpen }) {
  const [data, setData] = useState({ items: [], total: 0, statuses: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(presetStatus || "");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), limit: "25" });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    const res = await api.get(`/admin-ops/candidates?${params}`);
    setData(res.data);
  }, [page, q, status]);

  useEffect(() => { setStatus(presetStatus || ""); setPage(1); }, [presetStatus]);
  useEffect(() => {
    const t = setTimeout(() => load().catch((e) => setError(formatApiError(e))), 250);
    return () => clearTimeout(t);
  }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / 25));
  return (
    <div className="space-y-3">
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
      <div className="flex flex-wrap gap-2">
        <input data-testid="candidates-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Rechercher (nom, courriel, identifiant)…"
          className="flex-1 min-w-[220px] text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
        <select data-testid="candidates-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
          <option value="">Tous les statuts</option>
          {data.statuses.map((s) => <option key={s} value={s}>{statusLabel(s, "candidate")}</option>)}
        </select>
        <a data-testid="candidates-export" href={`${API}/api/admin-ops/export?type=candidates`}
          className="flex items-center gap-1.5 text-xs font-semibold text-brand-navy border border-slate-200 rounded-lg px-3.5 py-2.5 hover:border-brand-turquoise">
          <Download className="w-3.5 h-3.5" /> Exporter CSV
        </a>
      </div>
      <DataTable testid="candidates-table" rowKey="candidate_id" onRowClick={onOpen}
        empty="Aucun candidat ne correspond aux filtres."
        columns={[
          { key: "name", label: "Candidat", render: (r) => <span className="font-semibold text-brand-navy">{r.name}</span> },
          { key: "profession", label: "Profession" },
          { key: "country", label: "Pays" },
          { key: "experience_years", label: "Exp.", render: (r) => (r.experience_years ? `${r.experience_years} an(s)` : "—") },
          { key: "languages", label: "Langues", render: (r) => (r.languages || []).join(", ") || "—" },
          { key: "offer_title", label: "Offre concernée" },
          { key: "company", label: "Employeur" },
          { key: "applications_count", label: "Cand." },
          { key: "commercial_status", label: "Statut", render: (r) => (
            <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${STATUS_COLOR(r.commercial_status)}`}>
              {statusLabel(r.commercial_status, "candidate")}
            </span>
          ) },
          { key: "created_at", label: "Inscription", render: (r) => fmtDate(r.created_at) },
        ]}
        rows={data.items} />
      <Pager testid="candidates-pager" page={page} pages={pages} total={data.total} onPage={(p) => setPage(Math.min(Math.max(1, p), pages))} />
    </div>
  );
}
